import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Field, LogoField, useDirtyTracker } from "./_shared";
import { QRBlock } from "./QRBlock";
import { SplitFromGroupButton } from "./SplitFromGroupButton";
import { RenameSlugButton } from "./RenameSlugButton";

/* ---------------------------------------------------------------------------
 * LinkedPubEditor — the "linked pub" analogue of SingleLevelEditor.
 *
 * When a Brewery Group's admin picks one of its child (linked) pubs, this
 * form gives them the SAME data input surface as an Independent Pub:
 *   • Logo upload           (targets /pubs/[id]/logo)
 *   • Name / Town / Slug    (slug read-only)
 *   • Brand colour picker
 *   • Contact name / phone
 *   • KPI dashboard link
 *   • Notes
 *   • QR block for the pub's /[slug] landing
 *   • Split-from-group + Delete-pub actions
 *
 * Unlike SingleLevelEditor this form ONLY writes to the pub row — the
 * parent Group's fields (client doc) are untouched by design so per-pub
 * edits don't collide with a chain-level save.
 * ------------------------------------------------------------------------- */
export function LinkedPubEditor({ pub, onSaved, onDeleted, onLive, onDirtyChange }) {
  const [name, setName] = useState(pub.name || "");
  const [town, setTown] = useState(pub.town || "");
  const [color, setColor] = useState(pub.primary_color || "#0038FF");
  const [contactName, setContactName] = useState(pub.contact_name || "");
  const [contactPhone, setContactPhone] = useState(pub.contact_phone || "");
  const [kpiUrl, setKpiUrl] = useState(pub.kpi_url || "");
  const [notes, setNotes] = useState(pub.notes || "");
  const [logoUrl, setLogoUrl] = useState(pub.logo_url || null);
  const [saving, setSaving] = useState(false);

  // iter76 — unsaved-edits tracker for the details form. Amber pulse-dot
  // appears on the banner while `isDirty` is true; cleared via `commit()`
  // after save AND after switching to another linked pub (see below).
  const {
    isDirty: detailsDirty,
    commit: commitDetails,
  } = useDirtyTracker({
    name, town, color, contactName, contactPhone, kpiUrl, notes, logoUrl,
  });

  // Reset local state whenever the focused pub changes so switching pubs
  // in the group list doesn't leak the previous pub's edits.
  useEffect(() => {
    setName(pub.name || "");
    setTown(pub.town || "");
    setColor(pub.primary_color || "#0038FF");
    setContactName(pub.contact_name || "");
    setContactPhone(pub.contact_phone || "");
    setKpiUrl(pub.kpi_url || "");
    setNotes(pub.notes || "");
    setLogoUrl(pub.logo_url || null);
  }, [pub.id, pub.name, pub.town, pub.primary_color, pub.contact_name, pub.contact_phone, pub.kpi_url, pub.notes, pub.logo_url]);

  // Re-baseline the dirty tracker whenever we jump to a new pub so its
  // banner starts clean rather than showing a stale pulse-dot inherited
  // from the previously-edited pub. Fires AFTER the state setters above
  // have flushed a fresh render.
  useEffect(() => {
    commitDetails();
  }, [pub.id]);

  // iter78 — bubble the dirty flag up to the parent SectionCard so the
  // amber dot shows on the identity banner (which is our new toggle).
  useEffect(() => {
    onDirtyChange?.(detailsDirty);
  }, [detailsDirty, onDirtyChange]);

  // Bubble live-edit values up so the right-hand LivePreview stays in sync.
  useEffect(() => {
    onLive?.({ name, town, color, logoUrl, slug: pub.slug });
  }, [name, town, color, logoUrl, pub.slug, onLive]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await api.put(`/pubs/${pub.id}`, {
        name,
        town,
        contact_name: contactName,
        contact_phone: contactPhone,
        kpi_url: kpiUrl,
        notes,
        primary_color: color,
      });
      toast.success("Pub saved");
      commitDetails();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Remove pub "${pub.name}" from this group? Participants will be unlinked.`)) return;
    try {
      await api.delete(`/pubs/${pub.id}`);
      toast.success("Pub removed");
      onDeleted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div
      className="space-y-6"
      data-testid={`linked-pub-edit-${pub.id}`}
    >
      <LogoField
        current={logoUrl}
        endpointPost={`/pubs/${pub.id}/logo`}
        endpointDelete={logoUrl ? `/pubs/${pub.id}/logo` : null}
        onUpload={(data) => setLogoUrl(data.logo_url || null)}
        testId={`linked-pub-logo-${pub.id}`}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="linked-pub-name"
            className="h-10 rounded-sm border-zinc-300"
            required
          />
        </Field>
        <Field label="Town">
          <Input
            value={town}
            onChange={(e) => setTown(e.target.value)}
            data-testid="linked-pub-town"
            className="h-10 rounded-sm border-zinc-300"
          />
        </Field>
        <Field label="Slug" hint={`Landing URL: /${pub.slug || "…"}`}>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={pub.slug || ""}
              className="h-10 rounded-sm border-zinc-200 bg-zinc-50 font-mono text-sm flex-1 min-w-0"
              data-testid="linked-pub-slug"
            />
            {pub.id && pub.slug && (
              <RenameSlugButton pub={pub} onRenamed={onSaved} />
            )}
          </div>
        </Field>
        <Field label="Brand colour">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value.toUpperCase())}
              className="h-10 w-14 rounded-sm border border-zinc-300 cursor-pointer p-0.5"
            />
            <Input
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 rounded-sm border-zinc-300 font-mono uppercase text-sm"
              data-testid="linked-pub-color"
            />
          </div>
        </Field>
        <Field label="Contact name">
          <Input
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            data-testid="linked-pub-contact-name"
            className="h-10 rounded-sm border-zinc-300"
          />
        </Field>
        <Field label="Contact phone">
          <Input
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            data-testid="linked-pub-contact-phone"
            className="h-10 rounded-sm border-zinc-300"
          />
        </Field>
        <Field
          label="KPI dashboard link"
          hint="URL to a Metabase / Looker / private KPI page"
        >
          <Input
            value={kpiUrl}
            onChange={(e) => setKpiUrl(e.target.value)}
            placeholder="https://kpi.example.com/…"
            data-testid="linked-pub-kpi"
            className="h-10 rounded-sm border-zinc-300"
          />
        </Field>
        <Field label="Notes" testId="linked-pub-notes-field">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="linked-pub-notes"
            className="min-h-[80px] rounded-sm border-zinc-300"
          />
        </Field>
      </div>

      {/* QR + public link — under a subtle divider. */}
      <div className="pt-6 mt-2 border-t border-zinc-100">
        <QRBlock slug={pub.slug} name={name} />
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-zinc-100">
        <button
          type="button"
          onClick={del}
          data-testid="linked-pub-delete"
          className="text-xs font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1"
        >
          <Trash2 className="h-3.5 w-3.5" /> Remove from group
        </button>
        <div className="flex items-center gap-3">
          <SplitFromGroupButton pub={pub} onSplit={onSaved} />
          <Button
            type="button"
            onClick={submit}
            disabled={saving}
            data-testid="linked-pub-save"
            className="rounded-sm bg-zinc-950 hover:bg-black text-white h-10 px-6"
          >
            {saving ? "Saving…" : "Save pub"}
          </Button>
        </div>
      </div>
    </div>
  );
}
